import React, { useState } from 'react';
import { Check, Minus, Plus, X, AlertCircle } from 'lucide-react';

const WarehouseRejectModal = ({ order, transition, mode = 'REVISE', onClose, onSubmit, loading }) => {
    const isRevise = mode === 'REVISE';
    
    const [revisedItems, setRevisedItems] = useState(
        (order.items || []).map(item => ({
            ...item,
            // default to current quantity but allow change
            warehouseQuantity: item.warehouseQuantity ?? item.quantity 
        }))
    );
    
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');

    const updateQuantity = (idx, delta) => {
        setRevisedItems(prev => prev.map((item, i) => {
            if (i !== idx) return item;
            const currentQty = parseFloat(item.warehouseQuantity);
            const newQty = Math.max(0, (isNaN(currentQty) ? 0 : currentQty) + delta);
            return { ...item, warehouseQuantity: String(newQty) };
        }));
    };

    const handleQuantityChange = (idx, value) => {
        setRevisedItems(prev => prev.map((item, i) => {
            if (i !== idx) return item;
            return { ...item, warehouseQuantity: value };
        }));
    };

    const handleSave = () => {
        if (!notes.trim()) {
            setError('Please provide a reason/comment for this action.');
            return;
        }

        const payload = {
            items: revisedItems.map(item => ({
                productPublicId: item.productPublicId || item.id || item.publicId,
                warehouseQuantity: Math.max(0, parseFloat(item.warehouseQuantity) || 0)
            })),
            notes: notes.trim()
        };
        
        onSubmit(order, transition, payload);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn shadow-2xl" onClick={onClose}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-slideUp border border-gray-100 flex flex-col max-h-[82vh] sm:max-h-[calc(100dvh-2rem)]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`px-6 py-5 flex items-center justify-between border-b flex-shrink-0 ${isRevise ? 'bg-amber-50/50 border-amber-100' : 'bg-rose-50/50 border-rose-100'}`}>
                    <div>
                        <h2 className={`text-lg font-black tracking-tight ${isRevise ? 'text-amber-800' : 'text-rose-800'}`}>
                            {isRevise ? 'Suggest Revision' : 'Reject Order'}
                        </h2>
                        <p className="text-xs text-gray-500 font-medium mt-0.5">
                            Order {order.orderRefNo || `#${order.publicId?.slice(-6)}`} • {order.customer?.name}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-all">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto hidden-scrollbar min-h-0">
                    {/* Reason Field */}
                    <div className="mb-6">
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 px-1">
                            {isRevise ? 'Revision Notes / Reason' : 'Rejection Reason'} <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => {
                                setNotes(e.target.value);
                                if (error) setError('');
                            }}
                            placeholder={isRevise ? "E.g. Only part of the stock is available..." : "E.g. Items out of stock, customer requested cancellation..."}
                            className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border transition-all text-sm min-h-[100px] focus:ring-4 outline-none ${
                                error ? 'border-rose-300 focus:ring-rose-100' : 'border-gray-200 focus:ring-indigo-100 focus:border-indigo-400'
                            }`}
                        />
                        {error && (
                            <div className="flex items-center gap-1.5 mt-2 ml-1 text-rose-600">
                                <AlertCircle size={14} />
                                <span className="text-xs font-bold">{error}</span>
                            </div>
                        )}
                    </div>

                    {/* Item Adjustments */}
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">
                            Available Quantities {isRevise && <span className="text-amber-600 normal-case">(Mandatory adjustment)</span>}
                        </label>
                        <div className="space-y-3">
                            {revisedItems.map((item, idx) => (
                                <div key={idx} className="bg-gray-50/50 rounded-2xl p-3 border border-gray-100 flex items-center justify-between">
                                    <div className="min-w-0 pr-2">
                                        <p className="text-sm font-bold text-gray-800 truncate">{item.productName || 'Product'}</p>
                                        <p className="text-[10px] font-black text-indigo-600/60 uppercase tracking-tighter">
                                            Requested: {item.quantity} {item.unit || 'pcs'}
                                        </p>
                                    </div>
                                    
                                    <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden shrink-0">
                                        <button
                                            onClick={() => updateQuantity(idx, -1)}
                                            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        >
                                            <Minus size={16} strokeWidth={3} />
                                        </button>
                                        <input
                                            type="number"
                                            min="0"
                                            value={item.warehouseQuantity}
                                            onChange={(e) => handleQuantityChange(idx, e.target.value)}
                                            className="min-w-[72px] w-20 px-1 h-9 text-center text-sm font-black text-gray-900 border-x border-gray-100 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                        <button
                                            onClick={() => updateQuantity(idx, 1)}
                                            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        >
                                            <Plus size={16} strokeWidth={3} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50/80 border-t border-gray-100 flex gap-3 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3.5 rounded-2xl text-sm font-black text-gray-500 bg-white border border-gray-200 hover:bg-gray-100 transition-all uppercase tracking-widest"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className={`flex-1 py-3.5 rounded-2xl text-sm font-black text-white shadow-xl transition-all uppercase tracking-widest disabled:opacity-50 ${
                            isRevise ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-200' : 'bg-rose-500 hover:bg-rose-600 shadow-rose-200'
                        }`}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                            </div>
                        ) : (
                            isRevise ? 'Send to Sales' : 'Confirm Reject'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WarehouseRejectModal;
